// @ts-check

import { useMutation, useQuery } from "@tanstack/react-query"
import { atom, useAtom } from "jotai"
import { useSelector } from "react-redux"
import { createReview, getReview, listReviews } from "./reviews"

const ReviewQueryKeys = {
  /** @param {string} reviewId */
  REVIEW: (reviewId) => ["reviews", reviewId],
  /** @param {string} sessionId */
  SESSION_REVIEWS: (sessionId) => ["reviews", "session", sessionId],
}

/**
 * True while a review request is being created (which may involve creating a
 * brand-new chat session first). `useActiveChatSession` checks this so it
 * doesn't try to reattach to a chat stream that was never started for a
 * session created solely to hold a review's canvas document.
 */
const pendingReviewAtom = atom(false)

function usePendingReview() {
  return useAtom(pendingReviewAtom)
}

const PENDING_STATUSES = ["pending", "processing"]

function useUserToken() {
  // @ts-ignore
  const { user } = useSelector((state) => state.app.datastate)
  return user?.userToken ?? null
}

/**
 * Requests a new review. Resolves with `{ reviewId, documentId, status }`.
 */
function useCreateReview() {
  const userToken = useUserToken()
  return useMutation({
    /** @param {{ sessionId: string, files: Array<{ name: string, content: string }>, journals: string[], additionalContext: string }} payload */
    mutationFn: (payload) => createReview(payload, userToken),
  })
}

/**
 * Polls a review by id every 3s while it's pending/processing, stopping once
 * it reaches a terminal state ("complete" or "failed").
 * @param {string | null | undefined} reviewId
 */
function useReview(reviewId) {
  const userToken = useUserToken()
  return useQuery({
    queryKey: ReviewQueryKeys.REVIEW(reviewId ?? ""),
    queryFn: () => getReview(reviewId, userToken),
    enabled: reviewId != null && userToken != null,
    refetchInterval: (query) => (PENDING_STATUSES.includes(query.state.data?.status) ? 3000 : false),
  })
}

/**
 * Lists reviews for a session — lets callers know which canvas documents
 * also have a review report attached (by matching `documentId`).
 * @param {string | null | undefined} sessionId
 */
function useReviewsForSession(sessionId) {
  const userToken = useUserToken()
  return useQuery({
    queryKey: ReviewQueryKeys.SESSION_REVIEWS(sessionId ?? ""),
    queryFn: () => listReviews(sessionId, userToken),
    enabled: sessionId != null && userToken != null,
  })
}

export { ReviewQueryKeys, pendingReviewAtom, usePendingReview, useCreateReview, useReview, useReviewsForSession }
